import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AnyAction } from 'redux'
import { isPlainObject } from 'lodash-es'
import { ActionType } from 'typesafe-actions'
import { NAMESPACE } from './constants'
import { getTaggablePartyId } from './utils'
import {
  TAccountsRequest,
  TBulkImportResult,
  TPartyDetails,
  TPartyEntity,
  TPartyPayload,
  TPartyState,
  TPartyTagSummariesResponse,
  TQuickSearchParams,
  TQuickSearchSuccess,
  TSuspenseAccountFuzzyMatchesParams,
  TUpdatePartyRequest,
} from './types'
import { $TSFixMe } from 'types/ts-migrate'

type PartyCreateMeta = { initiator?: string }
type PartyFetchMeta = { id?: string }
type PartiesFetchMeta = { ids?: string[] }
type TIdentifiedPartyPayload = TPartyPayload & { id?: string }

const initialState: TPartyState = {
  isSearching: false,
  query: '',
  entities: {
    bySearchTerms: {},
    parties: {},
    partyTags: [],
    suspenseAccountFuzzyMatches: {}, // indexed by reference
  },
  createdByInitiator: {}, // indexed by initiator id
  pending: {}, // party ids with a fetch in flight, indexed by party id
  bulkImportResult: null,
}

/**
 * Fetch requests that populate `pending`. Used to release ids when a request fails —
 * an errored fetch never emits *_success, so without this the id stays "loading" forever.
 */
const PARTY_FETCH_REQUESTS = [`${NAMESPACE}/party_request`, `${NAMESPACE}/parties_request`]

const resolveId = (payload: TIdentifiedPartyPayload): string => getTaggablePartyId(payload)

/**
 * Merge a party payload over the entity already in state, one level into the
 * person/company subtree.
 *
 * A flat spread is not enough. Entities are shaped `{ person: {...}, id }` and accounts live
 * at `person.accounts`, so a partial payload — quickSearch results, and historically
 * /parties/bulk — replaces the whole subtree and silently drops anything a full
 * party_request had loaded. Merging per key keeps what the partial payload doesn't carry
 * while still applying everything it does.
 */
const mergePartyEntity = (existing: TPartyEntity | undefined, party: TPartyPayload, id: string): TPartyEntity => {
  const merged = { ...existing, id, ...party } as TPartyEntity & Record<string, unknown>
  const existingRecord = existing as Record<string, unknown> | undefined
  Object.entries(party as Record<string, unknown>).forEach(([key, nextValue]) => {
    const previousValue = existingRecord?.[key]
    if (isPlainObject(previousValue) && isPlainObject(nextValue)) {
      merged[key] = { ...(previousValue as TPartyDetails), ...(nextValue as TPartyDetails) }
    }
  })
  return merged
}

/** Guarded so a partial preloaded state (tests, older persisted state) can't break these. */
const markPending = (state: TPartyState, ids: string[]): void => {
  state.pending = state.pending || {}
  ids.forEach(id => {
    state.pending[id] = true
  })
}

const releasePending = (state: TPartyState, ids: string[]): void => {
  if (!state.pending) return
  ids.forEach(id => {
    delete state.pending[id]
  })
}

type THttpErrorAction = {
  type: 'http_error'
  payload: { response: $TSFixMe }
  meta: { initAction: AnyAction }
}

const isPartyFetchError = (action: AnyAction): action is THttpErrorAction =>
  action.type === 'http_error' && PARTY_FETCH_REQUESTS.includes(action.meta?.initAction?.type)

const slice = createSlice({
  name: NAMESPACE,
  initialState,
  reducers: {
    createParty_request: {
      reducer: (_state, _action: PayloadAction<TPartyPayload, string, PartyCreateMeta>) => {},
      prepare: (payload: TPartyPayload, meta: PartyCreateMeta = {}) => ({ payload, meta, error: null }),
    },
    createParty_success: {
      reducer: (state, { payload, meta }: PayloadAction<TIdentifiedPartyPayload, string, PartyCreateMeta>) => {
        const id = resolveId(payload)
        state.entities.parties[id] = { ...payload, id }
        if (meta?.initiator) {
          state.createdByInitiator[meta.initiator] = { ...payload, id }
        }
      },
      prepare: (payload: TIdentifiedPartyPayload, meta: PartyCreateMeta = {}) => ({ payload, meta, error: null }),
    },
    clearCreatedByInitiator: (state, { payload }: PayloadAction<string>) => {
      delete state.createdByInitiator[payload]
    },
    party_request: (state, { payload }: PayloadAction<string>) => {
      if (payload) {
        markPending(state, [payload])
      }
    },
    party_success: {
      reducer: (state, { payload, meta }: PayloadAction<TIdentifiedPartyPayload, string, PartyFetchMeta>) => {
        const id = resolveId(payload)
        state.entities.parties[id] = { ...payload, id }
        releasePending(state, [meta?.id || id])
      },
      prepare: (payload: TIdentifiedPartyPayload, meta: PartyFetchMeta = {}) => ({ payload, meta, error: null }),
    },
    updateParty_request: (state, { payload }: PayloadAction<TUpdatePartyRequest>) => {},
    updateParty_success: (state, { payload }: PayloadAction<TIdentifiedPartyPayload>) => {
      const id = resolveId(payload)
      state.entities.parties[id] = { ...payload, id }
    },
    partyTags_request: state => {},
    partyTags_success: (state, { payload }: PayloadAction<TPartyTagSummariesResponse>) => {
      state.entities.partyTags = payload.partyTagSummaries.map(tag => ({
        value: tag.name,
        label: tag.value,
      }))
    },
    parties_request: (state, { payload }: PayloadAction<string[]>) => {
      // Mirrors the in-state guard in fetchPartiesIfNotInState so we never mark an id
      // pending that the epic is going to skip. Both run against the same store snapshot
      // (reducers first, then the epic), so the two filters cannot disagree.
      const ids: string[] = Array.isArray(payload) ? payload : []
      markPending(
        state,
        ids.filter(id => !!id && !state.entities.parties[id]),
      )
    },
    parties_success: {
      reducer: (state, { payload, meta }: PayloadAction<TPartyPayload[], string, PartiesFetchMeta>) => {
        state.entities.parties = {
          ...state.entities.parties,
          ...payload.reduce((acc: { [partyId: string]: TPartyEntity }, party) => {
            const id = getTaggablePartyId(party)
            // Merge over any existing entity rather than replacing it, so top-level keys
            // written by other flows survive a bulk refresh. Note this is a *shallow* merge:
            // `person`/`company` come through wholesale, so nested data (accounts[]) is
            // preserved only because /parties/bulk now returns it — this merge never
            // protected it on its own.
            acc[id] = { ...state.entities.parties[id], id, ...party } as TPartyEntity
            return acc
          }, {}),
        }
        // Release every id the request asked for, not just the ones that came back — a
        // party the server has no record of would otherwise stay pending forever.
        const requestedIds: string[] = meta?.ids || payload.map(getTaggablePartyId)
        releasePending(state, requestedIds)
      },
      prepare: (payload: TPartyPayload[], meta: PartiesFetchMeta = {}) => ({ payload, meta, error: null }),
    },
    quickSearch_request: {
      reducer: (_state, _action: PayloadAction<TQuickSearchParams>) => {},
      prepare: ({ query = '', tags = '', offset = 0, limit = 10000 }: Partial<TQuickSearchParams>) => ({
        payload: { query, tags, offset, limit },
      }),
    },
    quickSearch_success: (state, { payload: { results, params } }: PayloadAction<TQuickSearchSuccess>) => {
      state.entities.bySearchTerms[params.query] = results.map(getTaggablePartyId)
      state.entities.parties = {
        ...state.entities.parties,
        ...results.reduce((acc: { [partyId: string]: TPartyEntity }, party) => {
          const id = getTaggablePartyId(party)
          // Search results are partial parties. This used to overwrite the entity outright,
          // so searching after opening a contact stripped the accounts[] its full fetch had
          // loaded — the account panels unmounted and balance_request stopped firing.
          acc[id] = mergePartyEntity(state.entities.parties[id], party, id)
          return acc
        }, {}),
      }
    },
    accounts_request: (state, { payload }: PayloadAction<TAccountsRequest>) => {},
    accounts_success: (state, { payload }: PayloadAction<$TSFixMe>) => {},
    suspenseAccountFuzzyMatches_request: (state, { payload }: PayloadAction<TSuspenseAccountFuzzyMatchesParams>) => {},
    suspenseAccountFuzzyMatches_success: {
      reducer: (
        state,
        { payload, meta }: PayloadAction<$TSFixMe, string, TSuspenseAccountFuzzyMatchesParams | undefined>,
      ) => {
        if (meta?.reference) {
          state.entities.suspenseAccountFuzzyMatches[meta.reference] = payload
        }
      },
      prepare: (payload: $TSFixMe, meta?: TSuspenseAccountFuzzyMatchesParams) => ({ payload, meta, error: null }),
    },
    bulkImport_request: (state, { payload }: PayloadAction<{ base64: string }>) => {},
    bulkImport_success: (state, { payload }: PayloadAction<TBulkImportResult>) => {
      state.bulkImportResult = payload
    },
    clearBulkImportResult: state => {
      state.bulkImportResult = null
    },
  },
  extraReducers: builder => {
    builder.addMatcher(isPartyFetchError, (state, action) => {
      const requested = action.meta.initAction.payload
      releasePending(state, Array.isArray(requested) ? requested : [requested])
    })
  },
})

const { reducer, actions: events } = slice

export type PartyEvent = ActionType<typeof events>

export { events, reducer }
